/**
 * tickets.ts
 *
 * API layer for the current user's purchased tickets for a given event.
 *
 * Endpoint:
 *  GET /event/purchased_events_ticket/{id}/   → paginated list of tickets bought for event {id}
 *
 * `id` is a required path parameter — the event's id.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api';
import { useUserStore } from '../store/useUserStore';

// ─── API types ───────────────────────────────────────────────────────────────

/**
 * `simple_ticket` and `medias` are documented as opaque readOnly strings, but
 * (like other SerializerMethodField-backed fields in this API) may come back
 * as richer values at runtime — so we keep them loosely typed and parse
 * defensively rather than assuming a shape.
 */
export interface ApiPurchasedEventTicket {
  id: number;
  ticket_index: number;
  check_in_time: string | null;
  recipient?: string | null;
  qr_code_url?: string | null;
  unique_id: string;
  checked_in: boolean;
  simple_ticket?: unknown;
  medias?: unknown;
}

export interface PaginatedApiPurchasedEventTicketList {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiPurchasedEventTicket[];
}

// ─── UI-friendly types ─────────────────────────────────────────────────────────

export interface PurchasedTicketItem {
  id: number;
  ticketIndex: number;
  uniqueId: string;
  checkedIn: boolean;
  checkInTime: string | null;
  recipientEmail: string | null;
  qrCodeUrl: string | null;
  /** Human-readable ticket label, derived from `simple_ticket`. */
  label: string;
  /** Event/ticket image, derived from `medias`, if any was provided. */
  imageUrl: string | null;
}

export interface PaginatedPurchasedTicketsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PurchasedTicketItem[];
}

export interface PurchasedTicketsListParams {
  eventId: string | number | undefined;
  page?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractTicketLabel(simpleTicket: unknown, ticketIndex: number): string {
  if (typeof simpleTicket === 'string' && simpleTicket.trim()) {
    return simpleTicket.trim();
  }
  if (simpleTicket && typeof simpleTicket === 'object') {
    const obj = simpleTicket as Record<string, unknown>;
    const candidate = obj.name ?? obj.title ?? obj.ticket_type;
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return `Ticket #${ticketIndex + 1}`;
}

function extractMediaUrl(media: unknown): string | undefined {
  if (!media) return undefined;
  if (typeof media === 'string') return media;
  if (typeof media === 'object') {
    const obj = media as Record<string, unknown>;
    const candidate = obj.file_url ?? obj.image ?? obj.file;
    if (typeof candidate === 'string' && candidate) return candidate;
  }
  return undefined;
}

function extractTicketImage(medias: unknown): string | null {
  if (Array.isArray(medias)) {
    for (const media of medias) {
      const url = extractMediaUrl(media);
      if (url) return url;
    }
    return null;
  }
  return extractMediaUrl(medias) ?? null;
}

export function mapApiPurchasedTicket(row: ApiPurchasedEventTicket): PurchasedTicketItem {
  return {
    id: row.id,
    ticketIndex: row.ticket_index,
    uniqueId: row.unique_id,
    checkedIn: !!row.checked_in,
    checkInTime: row.check_in_time || null,
    recipientEmail: row.recipient || null,
    qrCodeUrl: row.qr_code_url || null,
    label: extractTicketLabel(row.simple_ticket, row.ticket_index),
    imageUrl: extractTicketImage(row.medias),
  };
}

// ─── Queries ─────────────────────────────────────────────────────────────────

/** List the current user's purchased tickets for an event — GET /event/purchased_events_ticket/{id}/ */
export const useMyPurchasedTickets = (params: PurchasedTicketsListParams) => {
  const { isAuthenticated } = useUserStore();
  const { eventId } = params;

  return useQuery<PaginatedPurchasedTicketsResponse>({
    queryKey: ['purchased-tickets', eventId, params],
    enabled: isAuthenticated && !!eventId,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      const qs = searchParams.toString();
      const { data } = await apiClient.get<PaginatedApiPurchasedEventTicketList>(
        `/event/purchased_events_ticket/${eventId}/${qs ? `?${qs}` : ''}`,
      );
      const results = Array.isArray(data.results) ? data.results.map(mapApiPurchasedTicket) : [];
      return {
        count: data.count ?? results.length,
        next: data.next ?? null,
        previous: data.previous ?? null,
        results,
      };
    },
  });
};
